import type { JSX } from 'react';
import { dateLabel, type Copy, type Language } from './copy';
import {
  changedSections,
  modelChanges,
  modelFacts,
  type Settings,
  type SettingsSection,
} from './draft-changes';

// What customers get now and, when a draft is saved, what publishing it changes.
export function LiveSummary({
  live,
  draft,
  names,
  onOpen,
  copy,
  language,
}: {
  live: { at: string; configuration: Settings };
  draft: Settings | null;
  names: Record<string, string>;
  onOpen: (section: SettingsSection) => void;
  copy: Copy;
  language: Language;
}): JSX.Element {
  const { configuration } = live;
  const channels = (['chat', 'email'] as const)
    .filter((channel) => configuration.channels[channel].enabled)
    .map((channel) => copy[channel]);
  const services =
    configuration.appointmentServices?.map((service) => service.name) ??
    (configuration.calendar
      ? [configuration.calendar.serviceName ?? copy.appointment]
      : []);
  const changed = draft ? changedSections(configuration, draft) : [];
  const model = draft ? modelChanges(configuration, draft, copy) : [];
  return (
    <section className="live-summary" aria-label={copy.liveVersion}>
      <dl>
        <div>
          <dt>{copy.published}</dt>
          <dd>{dateLabel(live.at, language)}</dd>
        </div>
        {modelFacts(configuration, copy)
          .slice(0, 3)
          .map((fact) => (
            <div key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        <div>
          <dt>{copy.channels}</dt>
          <dd>{channels.join(', ') || copy.summaryNone}</dd>
        </div>
        <div>
          <dt>{copy.appointmentServices}</dt>
          <dd>{services.join(', ') || copy.summaryNone}</dd>
        </div>
      </dl>
      {draft && (
        <div className="draft-changes">
          <strong>
            {changed.length ? copy.draftChanges : copy.draftUnchanged}
          </strong>
          {changed.length > 0 && (
            <ul>
              {changed.map((section) => (
                <li key={section}>
                  <button type="button" onClick={() => onOpen(section)}>
                    {names[section]}
                  </button>
                  {section === 'model' &&
                    model.map((change) => (
                      <span key={change.label}>
                        {change.label}: {change.from} → {change.to}
                      </span>
                    ))}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
